import { observer } from "mobx-react";
import React from "react";
import { Icons } from "src/libs/assets";
import { nbsp } from "src/libs/constants";
import { tx } from "src/libs/i18n";
import { humanFileSize } from "src/libs/utils";
import { P } from "../Paragraph";
import * as styles from "./Filepicker.module.scss";

export type IFileType =
  | "image/jpeg"
  | "image/jpg"
  | "image/png"
  | "image/bmp"
  | "image/tiff"
  | "image/gif"
  | "application/pdf";

export interface FilepickerProps {
  id?: string;
  files: File[];
  onFilesUpdated: (files: File[]) => void;
  allowedFileTypes?: IFileType[];
}

const INPUT_ID = "Filepicker_input";

@observer
export class Filepicker extends React.Component<FilepickerProps> {
  onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles: File[] = [];
    for (let i = 0; i < e.target.files.length; i++) {
      this.validateAndAddFile(newFiles, e.target.files[i]);
    }
    this.addFiles(newFiles);
  };

  onDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  onDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();

    const items = e.dataTransfer.items;
    if (items) {
      const newFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          this.validateAndAddFile(newFiles, items[i].getAsFile());
        }
      }
      this.addFiles(newFiles);
    }
  };

  removeFile = (removeIdx: number) => {
    const { files, onFilesUpdated } = this.props;
    onFilesUpdated(files.filter((_, idx) => removeIdx != idx));
  };

  addFiles = (newFiles: File[]) => {
    const { files, onFilesUpdated } = this.props;
    if (newFiles.length) {
      onFilesUpdated(files.concat(newFiles));
    }
  };

  validateAndAddFile = (files: File[], file: File) => {
    const { allowedFileTypes } = this.props;
    if (!allowedFileTypes?.length || allowedFileTypes.includes(file.type as IFileType)) {
      files.push(file);
    } else {
      // TODO: Show error message for wrong file type?
      console.error(`${file.type} is invalid`);
    }
  };

  renderFile = (file: File, idx: number) => {
    const filetype = tx(`filetypes.${file.type}`).toUpperCase();
    const description = `${filetype} (${humanFileSize(file.size)})`;

    return (
      <div key={`${file.name}_${idx}`} className={styles.file}>
        <img src={Icons.file_text_small} alt="title-icon" />
        <div className={styles.fileContent}>
          <P medium className={styles.title}>
            {file.name}
          </P>
          <P small className={styles.description}>
            {description}
          </P>
        </div>
        <img
          className={styles.remove}
          onClick={() => this.removeFile(idx)}
          src={Icons.trash_small}
          alt="trash-icon"
        />
      </div>
    );
  };

  renderFiles = () => {
    const { files } = this.props;
    return files.map(this.renderFile);
  };

  render() {
    const { id } = this.props;

    const inputId = id || INPUT_ID;

    return (
      <div className={styles.wrapper}>
        <div
          className={styles.dropArea}
          onDrop={this.onDrop}
          onDragOver={this.onDragOver}
        >
          <input multiple type="file" hidden id={inputId} onChange={this.onFileChange} />
          <label htmlFor={inputId} className={styles.inputLabel}>
            <span>{tx("Filepicker.dropFilesHere")}</span>
            {nbsp}
            <span className={styles.browse}>{tx("Filepicker.browse")}</span>
          </label>
        </div>
        {this.renderFiles()}
      </div>
    );
  }
}
